import { RTCSession } from '@wui/jssip/lib/RTCSession'

import { useWebRTC } from '@/entities/webrtc'

import { getDomExceptionErrorType, MediaDevicesErrorType } from '@/shared/composables/dom-exception-error-type'
import { sendDTMFMediaConstraintsToSession } from '@/shared/utils/jssip'

import { activateLocalCameraStreamAtPrivateSession, activateLocalScreenSharingAtPrivateSession, deactivateLocalVideoStream, closePictureInPicture } from './local-media'

const getIsVideoTrackActiveInSession = (session: RTCSession) => {
  return session.connection.getSenders().some((sender) => {
    const track = sender.track
    return track?.kind === 'video' && track.enabled && track.readyState === 'live'
  })
}

export const switchCameraInPrivateSession = async () => {
  const facade = useWebRTC().currentRTCSessionFacade
  if (!facade.value) {
    throw new Error('No session')
  }
  const { isVideoMuted, isScreenSharing, localVideoStream, localScreenStream } = facade.value
  const hasActiveLocalVideoTrack = Boolean(localVideoStream.value?.getVideoTracks().some((track) => track.enabled && track.readyState === 'live'))
  const hasActiveVideoSender = getIsVideoTrackActiveInSession(facade.value.session)
  const isVideoMutedNow = !(hasActiveLocalVideoTrack && hasActiveVideoSender)
  const isScreenActiveNow = isScreenSharing.value

  try {
    if (isVideoMutedNow) {
      if (isScreenActiveNow) {
        closePictureInPicture()
        deactivateLocalVideoStream(localScreenStream, facade.value.session.connection)
      }
      await activateLocalCameraStreamAtPrivateSession(facade.value.session, localVideoStream)
    } else {
      deactivateLocalVideoStream(localVideoStream, facade.value.session.connection)
    }
    isVideoMuted.value = !isVideoMutedNow
    return sendDTMFMediaConstraintsToSession(
      facade.value.session,
      { video: isVideoMutedNow },
    )
  } catch (error) {
    return Promise.reject(error)
  }
}

export const switchScreenSharingInPrivateSession = async () => {
  const facade = useWebRTC().currentRTCSessionFacade
  if (!facade.value) {
    throw new Error('No session')
  }
  const { isScreenSharing, isVideoSharing, isVideoMuted, localScreenStream } = facade.value
  const isScreenMutedNow = !isScreenSharing.value
  const isVideoActiveNow = isVideoSharing.value

  try {
    if (isScreenMutedNow) {
      if (isVideoActiveNow) {
        await switchCameraInPrivateSession()
      }
      await activateLocalScreenSharingAtPrivateSession(facade.value.session, localScreenStream)
      const track = localScreenStream.value?.getVideoTracks().at(0)
      if (track) {
        track.addEventListener('ended', () => {
          const activeFacade = useWebRTC().currentRTCSessionFacade.value
          const currentTrack = activeFacade?.localScreenStream.value?.getVideoTracks().at(0)
          if (!currentTrack || currentTrack.id !== track.id) {
            return
          }
          switchScreenSharingInPrivateSession()
        }, { once: true })
      }
    } else {
      closePictureInPicture()
      deactivateLocalVideoStream(localScreenStream, facade.value.session.connection)
    }
    isVideoMuted.value = true
    return sendDTMFMediaConstraintsToSession(
      facade.value.session,
      { video: isScreenMutedNow },
    )
  } catch (error: unknown) {
    if (error instanceof DOMException) {
      const errorType = getDomExceptionErrorType(error)
      if (errorType === MediaDevicesErrorType.UserPermissionDenied && isVideoActiveNow) {
        switchCameraInPrivateSession()
      }
    }

    return Promise.reject(error)
  }
}
